import React from "react";
import { useFormContext } from "react-hook-form";
import { Dialog, Form } from "@sscs/ui";
import { GBToBytes, convertToReadable } from "@sscs/utils";
import { MAX_PERCENTAGE, MIN_PERCENTAGE } from "Utils";
import { PercentageSlider } from "../";
import styles from "./analysisCapacityFields.module.scss";
import { getPercentageHelperText } from "./utils";

type AnalysisCapacityFieldsProps = {
  groupsCount: number;
  filestreamUsageBytes: number;
  projectsUsageBytes: number;
  filestreamPercentageFieldName: string;
  projectsPercentageFieldName: string;
  filestreamPercentage: number | null;
  projectsPercentage: number | null;
  isRequired: boolean;
  onPercentageSliderChange: (value: number) => void;
  onProjectsPercentageInputChange: (value: string) => void;
  totalPortalCapacity?: number;
};

const AnalysisCapacityFields = ({
  groupsCount,
  filestreamUsageBytes,
  projectsUsageBytes,
  filestreamPercentageFieldName,
  projectsPercentageFieldName,
  filestreamPercentage,
  projectsPercentage,
  onPercentageSliderChange,
  onProjectsPercentageInputChange,
  isRequired,
  totalPortalCapacity,
}: AnalysisCapacityFieldsProps): React.JSX.Element => {
  const methods = useFormContext();

  const {
    formState: { errors },
    watch,
  } = methods;

  const limited_input = watch("limited");
  const limited = parseInt(limited_input);

  const totalPortalCapacityBytes =
    totalPortalCapacity !== undefined ? GBToBytes(totalPortalCapacity) : 0;
  const limitedBytes = GBToBytes(limited);
  const filestreamLimitBytes =
    filestreamPercentage !== null
      ? (filestreamPercentage / 100) * (limitedBytes ?? 0)
      : 0;
  const projectsLimitBytes =
    projectsPercentage !== null
      ? (projectsPercentage / 100) * (limitedBytes ?? 0)
      : 0;

  const isDisabled = groupsCount === 0;

  return (
    <>
      <div>
        <Form.TextField
          label={`Monthly limit ${groupsCount > 1 ? "per group " : ""}(GB)`}
          name="limited"
          id="group-analysis-capacity"
          type="number"
          helperText={
            totalPortalCapacityBytes
              ? `Maximum ${convertToReadable(totalPortalCapacityBytes)}`
              : undefined
          }
          errors={errors}
          unit="GB"
          inputProps={{
            min: isRequired ? 1 : 0,
            max: totalPortalCapacity,
            step: 1,
          }}
          isDisabled={isDisabled}
          isFullWidth
        />
      </div>
      <Form.FormRow>
        <Form.TextField
          label="File stream allocation (%)"
          name={filestreamPercentageFieldName}
          id="group-filestream-percentage"
          type="number"
          unit="%"
          errors={errors}
          helperText={getPercentageHelperText(filestreamPercentage, limited)}
          inputProps={{
            min: MIN_PERCENTAGE,
            max: MAX_PERCENTAGE,
            step: 1,
          }}
          isDisabled={isDisabled}
          className={styles.allocation}
        />
        <Form.TextField
          label="Projects allocation (%)"
          type="number"
          name={projectsPercentageFieldName}
          errors={errors}
          onChange={(event) =>
            onProjectsPercentageInputChange(event.currentTarget.value)
          }
          unit="%"
          helperText={getPercentageHelperText(projectsPercentage, limited)}
          inputProps={{
            min: MIN_PERCENTAGE,
            max: MAX_PERCENTAGE,
            step: 1,
            value: projectsPercentage === null ? "" : projectsPercentage,
          }}
          isDisabled={isDisabled}
          className={styles.allocation}
        />
      </Form.FormRow>
      <PercentageSlider
        value={filestreamPercentage}
        onChange={onPercentageSliderChange}
        firstLabel="File stream"
        secondLabel="Projects"
        disabled={isDisabled}
      />
      <div className={styles["warnings"]}>
        {limitedBytes > 0 && filestreamLimitBytes < filestreamUsageBytes && (
          <Dialog.Banner
            type="warning"
            title={`Current file stream usage (${convertToReadable(
              filestreamUsageBytes,
            )}) exceeds the file stream limit (${convertToReadable(
              filestreamLimitBytes,
            )})`}
          >
            New file uploads will be rejected due to limit already being
            exceeded.
          </Dialog.Banner>
        )}
        {limitedBytes > 0 && projectsLimitBytes < projectsUsageBytes && (
          <Dialog.Banner
            type="warning"
            title={`Current projects usage (${convertToReadable(
              projectsUsageBytes,
            )}) exceeds the projects limit (${convertToReadable(
              projectsLimitBytes,
            )})`}
          >
            New file uploads will be rejected due to limit already being
            exceeded.
          </Dialog.Banner>
        )}
      </div>
    </>
  );
};

export default AnalysisCapacityFields;
